import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, TrendingUp, Users, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoPMetricsCard({ headers }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cop/metrics`, { headers });
      setMetrics(res.data);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!metrics) return null;

  const rows = [
    {
      label: 'Sensing Diversity Index',
      value: metrics.sensing_diversity_index?.toFixed(2) ?? '—',
      target: `≥ ${metrics.sensing_diversity_target}`,
      pct: metrics.total_users > 0 ? (metrics.sensing_diversity_index / metrics.sensing_diversity_target) : 0,
      hint: `${metrics.distinct_capturers}/${metrics.total_users} members captured`,
      icon: Users,
    },
    {
      label: 'Time to first peer signal',
      value: metrics.time_to_first_peer_hours_median != null ? `${metrics.time_to_first_peer_hours_median}h` : '—',
      target: `≤ ${metrics.time_to_first_peer_target_hours}h`,
      pct: metrics.time_to_first_peer_hours_median != null
        ? Math.min(1, metrics.time_to_first_peer_target_hours / Math.max(1, metrics.time_to_first_peer_hours_median))
        : 0,
      hint: 'median across last 30d',
      icon: Clock,
    },
    {
      label: 'Novice ↔ Expert ratio',
      value: metrics.novice_expert_engagement_ratio != null ? metrics.novice_expert_engagement_ratio.toFixed(2) : '—',
      target: `≥ ${metrics.novice_expert_target}`,
      pct: metrics.novice_expert_engagement_ratio != null
        ? metrics.novice_expert_engagement_ratio / metrics.novice_expert_target
        : 0,
      hint: 'non-staff engagers / staff engagers',
      icon: TrendingUp,
    },
  ];

  return (
    <Card className="border-[#E5E7EB]" data-testid="cop-metrics-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#002FA7]" />CoP Vitality · last 30 days
            <Badge className="text-[9px] bg-[#F3F4F6] text-[#4B5563] border-0">
              {metrics.total_signals} signals · {metrics.resonant_signals} resonant
            </Badge>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="cop-metrics-refresh">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-3">
          {rows.map(r => {
            const Icon = r.icon;
            const met = r.pct >= 1;
            return (
              <div key={r.label} className="p-3 rounded-lg bg-[#F9FAFB]" data-testid={`metric-${r.label.replace(/\W+/g, '-').toLowerCase()}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className="w-3 h-3 text-[#9CA3AF]" />
                  <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">
                    {r.label}
                  </span>
                </div>
                <p className="text-xl font-bold text-[#0A0A0A]">{r.value}</p>
                <div className="w-full h-1.5 bg-white rounded-full overflow-hidden mt-1.5">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, (r.pct || 0) * 100)}%`,
                      backgroundColor: met ? '#34C759' : '#FF9500',
                    }}
                  />
                </div>
                <p className="text-[10px] text-[#4B5563] mt-1">
                  target {r.target} · {r.hint}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
